/**
 * IntelligentOrderDialog — Feb/2026
 *
 * Modal que permite al vendedor:
 *   1. Pegar texto de WhatsApp (lista cruda del cliente), O
 *   2. Subir foto de remisión manuscrita, O
 *   3. Ambos a la vez
 *
 * El backend invoca a Gemini 3 Flash para extraer items {cantidad, descripción}
 * y luego hace fuzzy match contra el catálogo real. El wizard muestra una
 * preview donde el vendedor confirma cada match, cambia a una alternativa,
 * ajusta cantidades, o descarta.
 *
 * Al confirmar, llama a `onConfirmar(items)` para que el PedidoMovil o el
 * builder normal lo use como estado inicial.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo, useDeferredValue } from "react";
import { fuzzySearch } from "@/lib/fuzzySearch";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X, Sparkles, Image as ImageIcon, Loader2, Check, AlertTriangle, Trash2, Mic, MicOff, Camera, Search } from "lucide-react";
import { api } from "@/lib/api";

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
    {children}
  </label>
);

export default function IntelligentOrderDialog({ open, onClose, onConfirmar, productos = [] }) {
  const [texto, setTexto] = useState("");
  // Feb/2026: soporte multi-imagen. Cada entrada: { b64, mime, preview }
  const [imagenes, setImagenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsParsed, setItemsParsed] = useState(null); // null = aún no parseado
  const [totalRemision, setTotalRemision] = useState(null);
  // Feb/2026: estado del picker de catálogo para reemplazar producto ambiguo/no encontrado
  const [pickerFor, setPickerFor] = useState(null); // { key, descripcion }
  const [pickerQuery, setPickerQuery] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null); // Feb/2026: input separado con capture="environment" para tomar foto directa
  // Voz: Web Speech API para dictar el texto del pedido
  const [dictando, setDictando] = useState(false);
  const recognitionRef = useRef(null);
  const dictInterimRef = useRef("");
  const dictWantRef = useRef(false); // true mientras el usuario quiere seguir dictando

  useEffect(() => {
    if (!open) {
      dictWantRef.current = false;
      try { recognitionRef.current?.stop(); } catch (e) { /* noop */ }
      setDictando(false);
    }
  }, [open]);

  // Mismo buscador local que el armado de pedidos (instantáneo, sin red).
  const pickerQueryDeferred = useDeferredValue(pickerQuery);
  const pickerResults = useMemo(() => {
    const q = (pickerQueryDeferred || "").trim();
    if (!pickerFor || q.length < 2) return [];
    return fuzzySearch(productos || [], q, ["nombre", "sku", "codigo", "familia_key", "categoria_nombre", "categoria", "descripcion", "color"], { limit: 40, boostFields: ["sku", "codigo"], strict: true });
  }, [pickerFor, pickerQueryDeferred, productos]);

  const toggleDictado = () => {
    if (dictando) {
      dictWantRef.current = false;
      try { recognitionRef.current?.stop(); } catch (e) { /* noop */ }
      setDictando(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta dictado de voz. Usa Chrome o Safari.");
      return;
    }
    // Feb/2026: diagnóstico robusto para fix "mic concedido pero no transcribe":
    // - mostrar errores reales con toast (no silenciar).
    // - en Chrome Android continuous=true a veces se corta tras cada pausa:
    //   reiniciamos automáticamente mientras `dictWantRef.current` sea true.
    // - usar dictInterimRef para preservar el texto final acumulado entre
    //   reinicios y no perder lo ya dictado.
    dictInterimRef.current = texto; // base acumulada
    dictWantRef.current = true;

    const startRec = () => {
      let rec;
      try {
        rec = new SpeechRecognition();
      } catch (e) {
        toast.error("No se pudo iniciar el dictado: " + (e?.message || e));
        setDictando(false);
        dictWantRef.current = false;
        return;
      }
      rec.lang = "es-CO";
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      rec.onresult = (ev) => {
        let interim = "";
        let final = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) final += r[0].transcript + " ";
          else interim += r[0].transcript;
        }
        if (final) {
          const base = dictInterimRef.current || "";
          dictInterimRef.current = (base + (base ? " " : "") + final.trim()).trim();
        }
        const base = dictInterimRef.current || "";
        setTexto((base + (interim ? (base ? " " : "") + interim : "")).trim());
      };

      rec.onerror = (ev) => {
        const err = ev?.error || "unknown";
        if (err === "no-speech") {
          // silencio: no cortar, dejar que onend reinicie si el usuario quiere seguir
          return;
        }
        if (err === "not-allowed" || err === "service-not-allowed") {
          toast.error("Permiso de micrófono denegado. Habilítalo en el navegador.");
          dictWantRef.current = false;
        } else if (err === "network") {
          toast.error("Dictado necesita internet (usa datos o WiFi).");
          dictWantRef.current = false;
        } else if (err === "aborted") {
          // detenido manualmente, no mostrar error
        } else {
          toast.error("Error de dictado: " + err);
        }
        setDictando(false);
      };

      rec.onend = () => {
        // Si el usuario quiere seguir y no hubo error grave, reiniciar
        // (Chrome Android corta cada ~30-60s o tras pausas largas).
        if (dictWantRef.current) {
          try {
            rec.start();
            return;
          } catch (e) {
            // ya corriendo o estado inválido: iniciar nueva instancia
            setTimeout(() => {
              if (dictWantRef.current) startRec();
            }, 150);
            return;
          }
        }
        setDictando(false);
      };

      try {
        rec.start();
        recognitionRef.current = rec;
        setDictando(true);
      } catch (e) {
        toast.error("No se pudo iniciar: " + (e?.message || e));
        dictWantRef.current = false;
        setDictando(false);
      }
    };

    startRec();
    toast.info("Dictando… Toca el micro para parar.", { duration: 2500 });
  };

  const resetAll = () => {
    setTexto(""); setImagenes([]); setItemsParsed(null); setTotalRemision(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const nuevas = [];
    let descartadas = 0;
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) { descartadas++; continue; }
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { descartadas++; continue; }
      nuevas.push(f);
    }
    if (descartadas > 0) toast.error(`${descartadas} imagen${descartadas === 1 ? "" : "es"} descartada${descartadas === 1 ? "" : "s"} (max 8MB · JPG/PNG/WEBP).`);
    // Jun/2026: redimensionamos a máx. 1800px y JPEG 0.85 antes de subir —
    // fotos de 4-12MP tardaban en subir y la IA no necesita más resolución.
    nuevas.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target.result;
        const img = new Image();
        img.onload = () => {
          const MAX = 1800;
          const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
          let b64 = String(result).split(",")[1];
          let mime = f.type;
          let preview = result;
          if (ratio < 1 || f.size > 1.2 * 1024 * 1024) {
            try {
              const c = document.createElement("canvas");
              c.width = Math.round(img.width * ratio); c.height = Math.round(img.height * ratio);
              c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
              preview = c.toDataURL("image/jpeg", 0.85);
              b64 = preview.split(",")[1];
              mime = "image/jpeg";
            } catch {}
          }
          setImagenes((prev) => [...prev, { b64, mime, preview, name: f.name }]);
        };
        img.onerror = () => setImagenes((prev) => [...prev, { b64: String(result).split(",")[1], mime: f.type, preview: result, name: f.name }]);
        img.src = result;
      };
      reader.readAsDataURL(f);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImagen = (idx) => {
    setImagenes((prev) => prev.filter((_, i) => i !== idx));
  };

  const parsear = useCallback(async () => {
    if (!texto.trim() && imagenes.length === 0) {
      toast.error("Pega el texto del cliente o sube una foto de la remisión.");
      return;
    }
    setLoading(true);
    const tid = toast.loading(imagenes.length > 1 ? `Analizando ${imagenes.length} imágenes con IA…` : "Analizando pedido con IA…");
    try {
      // Feb/2026: cuando hay múltiples imágenes, llamamos al endpoint una vez
      // por imagen y acumulamos los items detectados (el endpoint actual acepta
      // 1 imagen por request). Si hay texto, lo mandamos SOLO en la primera.
      const todosItems = [];
      let totalRem = null;
      let rawResp = null;
      const requests = imagenes.length === 0
        ? [{ texto: texto.trim() || null, imagen_base64: null, imagen_mime: "image/jpeg" }]
        : imagenes.map((img, i) => ({
            texto: i === 0 ? (texto.trim() || null) : null,
            imagen_base64: img.b64,
            imagen_mime: img.mime,
          }));
      // Jun/2026: las páginas se analizan EN PARALELO (3 hojas tardan lo que 1)
      // y los totales de cada hoja se suman.
      let listas = 0;
      const resultados = await Promise.all(requests.map((req, i) =>
        api.post("/pedidos/parse-inteligente", req, { timeout: 150000 }).then((r) => {
          listas += 1;
          if (requests.length > 1) toast.loading(`Hoja ${listas}/${requests.length} lista…`, { id: tid });
          return { i, data: r.data };
        })
      ));
      resultados.sort((a, b) => a.i - b.i);
      for (const { data } of resultados) {
        (data.items || []).forEach((it) => todosItems.push(it));
        if (data.total_remision) totalRem = (totalRem || 0) + Number(data.total_remision);
        rawResp = data.raw_llm_response || rawResp;
      }
      const items = todosItems.map((it, i) => ({
        ...it,
        _key: `item-${i}-${Date.now()}`,
        _sel_idx: it.matches && it.matches.length > 0 ? 0 : -1,
      }));
      setItemsParsed(items);
      setTotalRemision(totalRem);
      toast.success(`${items.length} items detectados. Revisa y confirma.`, { id: tid });
    } catch (e) {
      const code = e?.response?.status;
      const detalle = e?.response?.data?.detail;
      const msg = typeof detalle === "string" && detalle.length < 300 ? detalle : (
        code === 502 ? "La IA no pudo procesar la entrada. Reintenta." :
        "No se pudo analizar. Revisa texto/foto."
      );
      toast.error(msg, { id: tid });
    } finally {
      setLoading(false);
    }
  }, [texto, imagenes]);

  const toggleItem = (key) => {
    setItemsParsed((prev) => prev.map((it) => it._key === key ? { ...it, _descartado: !it._descartado } : it));
  };
  const changeMatch = (key, idx) => {
    setItemsParsed((prev) => prev.map((it) => it._key === key ? { ...it, _sel_idx: idx } : it));
  };
  // Feb/2026: reemplazar manualmente el producto de una línea con uno elegido
  // del catálogo (útil para líneas marcadas AMBIGUO o NO ENCONTRADO).
  const replaceWithCatalogProduct = (key, producto) => {
    setItemsParsed((prev) => prev.map((it) => {
      if (it._key !== key) return it;
      const nuevoMatch = {
        producto_id: producto.id,
        nombre: producto.nombre,
        color: producto.color,
        disponible: Math.max((producto.stock_actual || 0) - (producto.stock_reservado || 0), 0),
        precio_sugerido: producto.precio_sugerido || producto.precio_mayorista || producto.precio_detal || 0,
        imagen_path: producto.imagen_path,
        unidad: producto.unidad || "unidad",
      };
      // Lo ponemos como primera opción + seleccionado, dejando las sugerencias originales abajo.
      const otras = (it.matches || []).filter(m => m.producto_id !== producto.id);
      return { ...it, matches: [nuevoMatch, ...otras], _sel_idx: 0, nota: null };
    }));
  };
  const changeCantidad = (key, val) => {
    // Jun/2026: permite decimales (13,5 metros de guipiur)
    // Se guarda como texto mientras se escribe (para permitir "13.", "13.0", "13.05")
    // y se convierte a número al confirmar.
    let limpio = String(val).replace(",", ".").replace(/[^0-9.]/g, "");
    const i = limpio.indexOf(".");
    if (i >= 0) limpio = limpio.slice(0, i + 1) + limpio.slice(i + 1).replace(/\./g, "");
    setItemsParsed((prev) => prev.map((it) => it._key === key ? { ...it, cantidad: limpio } : it));
  };

  const confirmar = () => {
    const seleccionados = (itemsParsed || []).filter((it) => !it._descartado && it._sel_idx >= 0 && it.matches[it._sel_idx]);
    if (seleccionados.length === 0) {
      toast.error("No hay items válidos para agregar.");
      return;
    }
    const paraCarrito = seleccionados.map((it) => ({
      producto: it.matches[it._sel_idx],
      cantidad: Number(it.cantidad) > 0 ? Number(it.cantidad) : 1,
      descripcion_original: it.descripcion,
      precio_unitario_ia: it.precio_unitario || null,  // precio detectado por IA (si lo hubo)
    }));
    onConfirmar?.(paraCarrito);
    resetAll();
    onClose?.();
  };

  if (!open) return null;

  // Feb/2026: usamos Radix Dialog para el IA dialog para que, al abrirse
  // encima del PedidoBuilder (también Radix), transfiera el focus-trap
  // correctamente. Antes el PedidoBuilder robaba el foco de vuelta y los
  // inputs no respondían. Mantenemos data-ia-overlay para que el padre
  // (PedidoBuilder) sepa no cerrarse al interactuar con el IA.
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { resetAll(); onClose?.(); } }}>
      <DialogContent
        data-ia-overlay
        className="max-w-2xl p-0 overflow-hidden z-[71]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <VisuallyHidden><DialogTitle>Pedido inteligente</DialogTitle></VisuallyHidden>
        <div className="relative bg-white dark:bg-slate-900 w-full max-h-[90vh] flex flex-col overflow-hidden" data-testid="intel-order-dialog">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-primary/10 to-fuchsia-500/10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Pedido inteligente</h2>
          </div>
          <button onClick={() => { resetAll(); onClose?.(); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="intel-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!itemsParsed ? (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Pega el texto del WhatsApp del cliente <strong>o</strong> sube una foto de la remisión manuscrita (o ambos). La IA extraerá los items y los va a matchear con tu catálogo.
            </div>

            <Field label="Texto del cliente (opcional)">
              <div className="relative">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Hola, mándame por favor:&#10;- 20 cintas satin verde 2.5cm a 3500&#10;- 15 de la roja de 4cm"
                  className="w-full min-h-[140px] rounded-lg border-2 border-slate-200 focus:border-primary focus:outline-none bg-white dark:bg-slate-800 text-sm p-3 pr-14 resize-y"
                  data-testid="intel-texto-input"
                />
                <button
                  type="button"
                  onClick={toggleDictado}
                  className={`absolute top-2 right-2 p-2.5 rounded-full shadow-md transition ${dictando ? "bg-rose-500 text-white animate-pulse" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                  title={dictando ? "Parar dictado" : "Dictar por voz"}
                  data-testid="intel-dictado-btn"
                >
                  {dictando ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              </div>
            </Field>

            <Field label={`Foto${imagenes.length !== 1 ? "s" : ""} de remisión manuscrita (opcional)${imagenes.length > 0 ? ` · ${imagenes.length}` : ""}`}>
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onFile} className="hidden" data-testid="intel-foto-input" />
                {/* Feb/2026: input separado con capture — abre la cámara en vez de la galería.
                    Las fotos tomadas acá NO se guardan en la galería del teléfono. */}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="intel-camara-input" />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    className="py-4 px-2 border-2 border-dashed border-primary/40 bg-primary/5 rounded-lg hover:border-primary hover:bg-primary/10 flex flex-col items-center justify-center gap-1 text-sm text-primary font-semibold transition"
                    data-testid="intel-camara-btn"
                  >
                    <Camera className="w-6 h-6" />
                    <span className="text-xs">Tomar foto</span>
                    <span className="text-[10px] font-normal text-primary/70">(no se guarda en tu galería)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="py-4 px-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-primary hover:bg-primary/5 flex flex-col items-center justify-center gap-1 text-sm text-slate-600 font-medium transition"
                    data-testid="intel-foto-btn"
                  >
                    <ImageIcon className="w-6 h-6" />
                    <span className="text-xs">Elegir de galería</span>
                    <span className="text-[10px] font-normal text-slate-500">múltiples fotos</span>
                  </button>
                </div>
                {imagenes.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {imagenes.length} foto{imagenes.length === 1 ? "" : "s"} adjunta{imagenes.length === 1 ? "" : "s"}
                  </div>
                )}
                {imagenes.length > 0 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {imagenes.map((img, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-slate-200" data-testid={`intel-foto-preview-${i}`}>
                        <img src={img.preview} alt={`remision-${i}`} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeImagen(i)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center shadow"
                          title="Quitar"
                          data-testid={`intel-foto-remove-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            <button
              onClick={parsear}
              disabled={loading || (!texto.trim() && imagenes.length === 0)}
              className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
              data-testid="intel-parse-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {loading ? "Analizando…" : (imagenes.length > 1 ? `Analizar ${imagenes.length} imágenes con IA` : "Analizar con IA")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {(() => {
                // Oct/2026: detectar líneas que apuntan al mismo producto_id
                // para avisar visualmente al vendedor ANTES de confirmar.
                const activos = itemsParsed.filter(x => !x._descartado && x._sel_idx >= 0);
                const porPid = {};
                for (const x of activos) {
                  const pid = x.matches?.[x._sel_idx]?.producto_id;
                  if (!pid) continue;
                  porPid[pid] = (porPid[pid] || 0) + 1;
                }
                const dupCount = Object.values(porPid).filter(c => c > 1).reduce((a, c) => a + c, 0);
                if (dupCount > 0) {
                  return (
                    <div className="text-xs bg-amber-50 border-2 border-amber-300 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="intel-duplicate-warning">
                      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-amber-900">{dupCount} líneas apuntan al mismo producto.</strong>
                        <div className="text-amber-800 mt-0.5">Cambia la variante en las filas con fondo amarillo o elimínalas. Si las dejas así, se SUMARÁN sus cantidades al agregar.</div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <strong>{itemsParsed.filter(it => !it._descartado && it._sel_idx >= 0).length}</strong> items listos. Toca cualquier producto sugerido para cambiar la variante, ajusta cantidades o descarta items que no quieras.
                {totalRemision ? <div className="mt-1 text-emerald-700 font-bold">💰 Total detectado en remisión: ${totalRemision.toLocaleString("es-CO")}</div> : null}
              </div>
              {(() => {
                // Mapa de producto_id → cuántas veces aparece como match seleccionado
                const activosMap = {};
                for (const x of itemsParsed) {
                  if (x._descartado || x._sel_idx < 0) continue;
                  const pid = x.matches?.[x._sel_idx]?.producto_id;
                  if (pid) activosMap[pid] = (activosMap[pid] || 0) + 1;
                }
                return itemsParsed.map((it) => {
                  const sel = it._sel_idx >= 0 ? it.matches[it._sel_idx] : null;
                  const esDuplicado = sel && activosMap[sel.producto_id] > 1 && !it._descartado;
                  const bordeColor = it._descartado ? "border-slate-200 bg-slate-50 opacity-50" :
                    esDuplicado ? "border-amber-400 bg-amber-50 ring-2 ring-amber-300" :
                    it.nota === "no_encontrado" ? "border-rose-300 bg-rose-50" :
                    it.nota === "ambiguo" ? "border-amber-300 bg-amber-50" :
                    "border-emerald-300 bg-emerald-50";
                  return (
                  <div key={it._key} className={`border-2 rounded-xl p-3 ${bordeColor}`} data-testid={`intel-item-${it._key}${esDuplicado ? '-dup' : ''}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap sm:flex-nowrap">
                      <input
                        type="text" inputMode="numeric" value={it.cantidad}
                        onChange={(e) => changeCantidad(it._key, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onBlur={() => { if (!(Number(it.cantidad) > 0)) changeCantidad(it._key, "1"); }}
                        className="w-16 h-8 rounded border-2 border-slate-300 bg-white text-center font-bold text-sm"
                        data-testid={`intel-cant-${it._key}`}
                      />
                      <span className="text-[12px] sm:text-sm font-medium text-slate-700 flex-1 min-w-0 break-words leading-tight">"{it.descripcion}"</span>
                      {it.precio_unitario ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-200 text-emerald-900 rounded whitespace-nowrap" title="Precio detectado en el texto/foto">
                          ${it.precio_unitario.toLocaleString("es-CO")} c/u
                        </span>
                      ) : null}
                      {esDuplicado && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-400 text-amber-900 rounded whitespace-nowrap">DUPLICADO</span>}
                      {!esDuplicado && it.nota === "ambiguo" && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded">AMBIGUO</span>}
                      {it.nota === "no_encontrado" && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-rose-200 text-rose-900 rounded">NO ENCONTRADO</span>}
                      <button
                        onClick={() => { setPickerFor({ key: it._key, descripcion: it.descripcion }); setPickerQuery(it.descripcion || ""); }}
                        className="p-1.5 rounded bg-primary/10 hover:bg-primary/20"
                        title="Buscar producto manualmente en el catálogo"
                        data-testid={`intel-picker-${it._key}`}
                      >
                        <Search className="w-3.5 h-3.5 text-primary" />
                      </button>
                      <button onClick={() => toggleItem(it._key)} className={`p-1.5 rounded ${it._descartado ? "bg-slate-200" : "bg-rose-100 hover:bg-rose-200"}`} data-testid={`intel-descartar-${it._key}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-700" />
                      </button>
                    </div>
                    {it.matches && it.matches.length > 0 ? (
                      <div className="space-y-1">
                        {it.matches.map((m, idx) => (
                          <button
                            key={m.producto_id}
                            onClick={() => changeMatch(it._key, idx)}
                            className={`w-full text-left px-2 py-1.5 rounded-lg border-2 text-xs flex items-center gap-2 transition ${it._sel_idx === idx ? "border-primary bg-primary/10" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                            data-testid={`intel-match-${it._key}-${idx}`}
                          >
                            {it._sel_idx === idx && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                            <span className="font-semibold flex-1 min-w-0 break-words leading-tight text-[11px] sm:text-xs">{m.nombre}</span>
                            <span className="text-slate-500 flex-shrink-0">{m.disponible} disp.</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-rose-700 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />No se encontró en el catálogo. Agrega manualmente luego.</div>
                    )}
                  </div>
                );
                });
              })()}
            </div>
            <div className="border-t p-3 flex gap-2 bg-white dark:bg-slate-900">
              <button onClick={() => setItemsParsed(null)} className="flex-1 py-2.5 rounded-lg border-2 border-slate-200 text-sm font-semibold hover:bg-slate-50" data-testid="intel-volver-btn">
                Volver a analizar
              </button>
              <button onClick={confirmar} className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 flex items-center justify-center gap-2" data-testid="intel-confirmar-btn">
                <Check className="w-4 h-4" /> Agregar al pedido
              </button>
            </div>
          </>
        )}
        {pickerFor && (
          <div className="absolute inset-0 z-20 bg-white dark:bg-slate-900 flex flex-col" data-testid="intel-picker-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="min-w-0">
                <div className="text-base font-bold">Elegir producto del catálogo</div>
                <div className="text-xs text-muted-foreground break-words">Reemplaza <span className="font-semibold">"{pickerFor.descripcion}"</span></div>
              </div>
              <button type="button" onClick={() => { setPickerFor(null); setPickerQuery(""); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="intel-picker-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative px-4 pt-3">
              <Search className="w-4 h-4 absolute left-7 top-1/2 translate-y-[-25%] text-slate-400" />
              <input
                type="text"
                autoFocus
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Buscar por nombre, código (EF26-192), SKU o color..."
                className="w-full h-11 pl-9 pr-3 rounded-lg border-2 border-slate-200 focus:border-primary focus:outline-none bg-white dark:bg-slate-800 text-base text-slate-900 dark:text-slate-100"
                data-testid="intel-picker-input"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {pickerQuery.trim().length < 2 && (
                <div className="text-center text-xs text-muted-foreground py-6">Escribe al menos 2 caracteres para buscar.</div>
              )}
              {pickerQuery.trim().length >= 2 && pickerResults.length === 0 && (
                <div className="text-center text-xs text-muted-foreground py-6">Sin resultados para "<b>{pickerQuery}</b>".</div>
              )}
              {pickerResults.map((p) => {
                const disp = Math.max((p.stock_actual || 0) - (p.stock_reservado || 0), 0);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      replaceWithCatalogProduct(pickerFor.key, p);
                      setPickerFor(null);
                      setPickerQuery("");
                      toast.success("Producto reemplazado");
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg border-2 border-slate-200 hover:border-primary hover:bg-primary/5 flex items-center gap-3 text-sm transition-colors"
                    data-testid={`intel-picker-result-${p.id}`}
                  >
                    {p.imagen_path ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/catalogo/img/${p.id}?w=80&v=${p.imagen_actualizada_at || 0}`} alt="" className="w-10 h-10 rounded object-cover border border-border flex-shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-slate-200 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold break-words leading-tight text-[12px] sm:text-sm">{p.nombre}</div>
                      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                        {p.codigo && <span>{p.codigo}</span>}
                        {p.sku && <span>· {p.sku}</span>}
                        {p.color && <span>· {p.color}</span>}
                      </div>
                    </div>
                    <div className={`text-xs font-semibold shrink-0 ${disp > 0 ? "text-emerald-700" : "text-rose-600"}`}>
                      {disp} disp.
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </DialogContent>

    </Dialog>
  );
}
